// Local dev browses `<tenant>.localhost:3000`, but `portals.frontendUrl` holds real deployed hosts,
// so a dev origin has to be translated to the staging portal it stands in for.
export const localDevelopmentBaseDomain = "staging.cube.ms";

// FULLY ANCHORED, and it must stay that way: `origin` is a client-supplied header, so an unanchored
// test would let `localhost.evil.com` (or `evil-localhost`) select a portal.
const LOCALHOST_HOST = /^(?:(.+)\.)?localhost(?::\d+)?$/;

function bareHost(host: string | undefined): string {
  return (host ?? "")
    .replace(/^https?:\/\//, "")
    .replace(/\/.*$/, "")
    .toLowerCase();
}

function portalForDevHost(host: string): string {
  const match = LOCALHOST_HOST.exec(bareHost(host));
  if (!match) return "";
  const [, tenant] = match;
  return tenant ? `${tenant}.${localDevelopmentBaseDomain}` : localDevelopmentBaseDomain;
}

/**
 * Server-side portal resolution for a request `Origin`. `undefined` means "no translation" — the
 * caller falls through to its real `frontendUrl` lookup.
 *
 * The dev mapping is GATED on `ENVIRONMENT`: `origin` is client-supplied and this runs before the
 * real lookup, so deployed it could only ever short-circuit a resolvable request into a wrong
 * portal or none. `ai.cube.ms` is deliberately OUTSIDE that gate — it is a real demo host that must
 * resolve to the techture portal in every environment, not a dev shortcut.
 *
 * Note the env var: `ENVIRONMENT` is server-only. The web half below reads `REACT_APP_ENV`, which
 * is why the two are separate exports rather than one shared function — a server var inside the
 * shared path evaluates to undefined under Vite and silently disables the web's dev mapping.
 */
export function resolvePortalURLBackend(origin: string | undefined): string | undefined {
  const bare = bareHost(origin);
  if (bare === "ai.cube.ms" || bare.endsWith(".ai.cube.ms")) {
    return `techture.${localDevelopmentBaseDomain}`;
  }
  if (process.env.ENVIRONMENT !== "development") return undefined;
  // `portalForDevHost` answers "" for a non-dev host; normalise to `undefined` so this function
  // matches its own signature and every caller's `if (result)` reads the same either way.
  return portalForDevHost(bare) || undefined;
}

/** Browser-side counterpart. Falls back to the host itself, which is what a deployment matches on. */
export function resolvePortalURLFrontend(host: string): string {
  return process.env.REACT_APP_ENV === "development" ? portalForDevHost(host) || host : host;
}
